import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ScanLine } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../../lib/auth';
import { Logo } from '../../brand';
import { FoundingCounterPill } from '../../components/FoundingCounter';

const NAV = [
    { to: '/', label: 'Home' },
    { to: '/pricing', label: 'Pricing' },
    { to: '/trust', label: 'Trust' },
    { to: '/contact', label: 'Contact' },
];

export function PublicLayout({ children }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const loc = useLocation();
    useEffect(() => { setOpen(false); }, [loc.pathname]);
    return (
        <div className="min-h-screen flex flex-col bg-background app-noise" data-testid="public-layout">
            {/* Nav */}
            <header className="sticky top-0 z-30 border-b border-border/60 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
                        <Logo variant="icon" size={30} />
                        <div className="font-heading text-lg leading-none">Ease My Bill</div>
                    </Link>
                    <nav className="hidden md:flex items-center gap-6">
                        {NAV.map(n => (
                            <Link
                                key={n.to}
                                to={n.to}
                                data-testid={`nav-${n.label.toLowerCase()}`}
                                className={`text-sm hover:text-primary transition-colors ${loc.pathname === n.to ? 'text-foreground font-medium' : 'text-muted-foreground'}`}
                            >
                                {n.label}
                            </Link>
                        ))}
                    </nav>
                    <div className="hidden md:flex items-center gap-2">
                        {user ? (
                            <Button asChild data-testid="nav-open-app">
                                <Link to="/dashboard">Open app</Link>
                            </Button>
                        ) : (
                            <>
                                <Button asChild variant="ghost" size="sm" data-testid="nav-login">
                                    <Link to="/login">Log in</Link>
                                </Button>
                                <Button asChild size="sm" data-testid="nav-signup">
                                    <Link to="/login?tab=signup">Start free trial</Link>
                                </Button>
                            </>
                        )}
                    </div>
                    <button
                        type="button"
                        className="md:hidden p-2 rounded hover:bg-muted"
                        onClick={() => setOpen(v => !v)}
                        aria-label="Toggle menu"
                        data-testid="nav-toggle"
                    >
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
                {open && (
                    <div className="md:hidden border-t border-border/60 bg-background" data-testid="nav-mobile">
                        <nav className="flex flex-col p-4 gap-3">
                            {NAV.map(n => (
                                <Link key={n.to} to={n.to} className="text-sm text-foreground">
                                    {n.label}
                                </Link>
                            ))}
                            <div className="flex gap-2 pt-2">
                                {user ? (
                                    <Button asChild className="flex-1"><Link to="/dashboard">Open app</Link></Button>
                                ) : (
                                    <>
                                        <Button asChild variant="outline" className="flex-1"><Link to="/login">Log in</Link></Button>
                                        <Button asChild className="flex-1"><Link to="/login?tab=signup">Start free</Link></Button>
                                    </>
                                )}
                            </div>
                        </nav>
                    </div>
                )}
            </header>

            {/* Body */}
            <main className="flex-1">{children}</main>
            <FoundingCounterPill />

            {/* Footer */}
            <footer className="border-t border-border/60 bg-muted/30" data-testid="public-footer">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
                    <div className="col-span-2">
                        <div className="flex items-center gap-2 mb-3">
                            <Logo variant="icon" size={26} />
                            <div className="font-heading text-base">Ease My Bill</div>
                        </div>
                        <p className="text-sm text-muted-foreground max-w-md">
                            The smartest way for Indian businesses to track expenses. Scan bills, catch price hikes, export CA-ready books.
                        </p>
                        <div className="mt-5 space-y-1 text-xs text-muted-foreground">
                            <div>37 Vijay Chowk, Inside Nagori Gate, Jodhpur, Rajasthan – 342001, India</div>
                            <div>
                                <a href="mailto:support@easemybill.com" className="hover:text-primary">support@easemybill.com</a>
                                <span className="mx-1.5">·</span>
                                <a href="tel:+919799119994" className="hover:text-primary">+91 97991 19994</a>
                            </div>
                            <div>PAN: <span className="font-mono">BYNPD9659J</span></div>
                        </div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Product</div>
                        <ul className="space-y-2 text-sm">
                            <li><Link to="/" className="hover:text-primary">Features</Link></li>
                            <li><Link to="/pricing" className="hover:text-primary">Pricing</Link></li>
                            <li><Link to="/login" className="hover:text-primary">Log in</Link></li>
                        </ul>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Legal</div>
                        <ul className="space-y-2 text-sm">
                            <li><Link to="/terms" data-testid="footer-terms" className="hover:text-primary">Terms of Service</Link></li>
                            <li><Link to="/privacy" data-testid="footer-privacy" className="hover:text-primary">Privacy Policy</Link></li>
                            <li><Link to="/trust" data-testid="footer-trust" className="hover:text-primary">Trust &amp; Security</Link></li>
                            <li><Link to="/refund" data-testid="footer-refund" className="hover:text-primary">Refund Policy</Link></li>
                            <li><Link to="/contact" data-testid="footer-contact" className="hover:text-primary">Contact Us</Link></li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-border/60">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 text-xs text-muted-foreground flex flex-col sm:flex-row items-center justify-between gap-2">
                        <div>© {new Date().getFullYear()} Ease My Bill. All rights reserved.</div>
                        <div>Made in India · Prices in ₹</div>
                    </div>
                </div>
            </footer>
        </div>
    );
}

/** Prose page shell used by all legal pages (Terms, Privacy, Refund, Contact). */
export function LegalPage({ title, updated, children, testid = 'legal-page' }) {
    return (
        <PublicLayout>
            <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid={testid}>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Ease My Bill · Legal</div>
                <h1 className="font-heading text-3xl sm:text-4xl mb-2">{title}</h1>
                {updated && (
                    <div className="text-xs text-muted-foreground mb-8">Last updated: {updated}</div>
                )}
                <div className="prose prose-slate max-w-none text-sm leading-7 [&_h2]:font-heading [&_h2]:text-xl [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:font-heading [&_h3]:text-base [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_li]:mb-1">
                    {children}
                </div>
            </div>
        </PublicLayout>
    );
}
